import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PropTypes from "prop-types";

import ButtonV1 from "components/ButtonV1";
import useToaster from "hooks/useToaster";
import { searchVendorLocations } from "services/eddService";
import { VENDOR_SEARCH_MIN_CHARS } from "constants/eddConfig";
import { isValidVendorId } from "utils/eddUtils";

const VendorSearch = ({ vendor, onVendorChange }) => {
  const triggerToaster = useToaster();
  const debounceTimerRef = useRef(null);
  const latestQueryRef = useRef("");

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  useEffect(() => () => clearTimeout(debounceTimerRef.current), []);

  const handleVendorSelect = (option) => {
    if (option) {
      setSearchTerm(option.vendor_id);
      setIsOptionsOpen(false);
    }
    onVendorChange(option || null);
  };

  const runSearch = async (query) => {
    latestQueryRef.current = query;
    setIsSearching(true);
    const result = await searchVendorLocations(query);
    setIsSearching(false);
    // text changed while request was in flight, drop stale results
    if (query !== latestQueryRef.current) return;

    if (result.error) {
      triggerToaster(result.error, "error");
      setVendorOptions([]);
      return;
    }
    setVendorOptions(result.data);
    if (!result.data.length) {
      triggerToaster("No vendor found", "info");
      return;
    }
    setIsOptionsOpen(true);
  };

  const handleSearch = () => {
    clearTimeout(debounceTimerRef.current);
    const query = searchTerm.trim();
    if (!query) {
      triggerToaster("Please enter a Vendor ID to search", "info");
      return;
    }
    if (!isValidVendorId(query)) {
      triggerToaster("Please enter a valid Vendor ID", "error");
      return;
    }
    runSearch(query);
  };

  const handleSearchInputChange = (_, value, reason) => {
    if (reason === "clear") {
      clearTimeout(debounceTimerRef.current);
      latestQueryRef.current = "";
      setSearchTerm("");
      setVendorOptions([]);
      setIsOptionsOpen(false);
      return;
    }
    if (reason !== "input") return;
    setSearchTerm(value);
    clearTimeout(debounceTimerRef.current);
    const query = value.trim();
    // options always belong to current text, fresh search repopulates them
    latestQueryRef.current = query;
    setVendorOptions([]);
    setIsOptionsOpen(false);
    if (query.length >= VENDOR_SEARCH_MIN_CHARS && isValidVendorId(query)) {
      debounceTimerRef.current = setTimeout(() => runSearch(query), 500);
    }
  };

  const handleSearchKeyDown = (e) => {
    // Enter on a highlighted option is handled by Autocomplete itself
    if (e.key === "Enter" && !(isOptionsOpen && vendorOptions.length)) {
      e.preventDefault();
      handleSearch();
    }
  };

  const renderVendorOption = (props, option) => (
    <li {...props} key={option.vendor_id}>
      <Box>
        <Typography fontSize={14} fontWeight={600} color="primary">
          {option.vendor_id}
        </Typography>
        <Typography fontSize={14}>{option.location_name || "-"}</Typography>
        <Typography fontSize={12} color="text.secondary">
          {[option.city, option.state].filter(Boolean).join(" | ") || "-"}
        </Typography>
      </Box>
    </li>
  );

  return (
    <Box>
      <Typography fontSize={18} fontWeight="bold" mb={4}>
        Select vendor
      </Typography>
      <Box display="flex" alignItems="center" gap={4}>
        <Autocomplete
          size="small"
          options={vendorOptions}
          value={vendor}
          inputValue={searchTerm}
          open={isOptionsOpen}
          onOpen={() => vendorOptions.length && setIsOptionsOpen(true)}
          onClose={() => setIsOptionsOpen(false)}
          onChange={(_, option) => handleVendorSelect(option)}
          onInputChange={handleSearchInputChange}
          getOptionLabel={(option) => option?.vendor_id ?? ""}
          isOptionEqualToValue={(option, value) =>
            option.vendor_id === value?.vendor_id
          }
          filterOptions={(options) => options}
          renderOption={renderVendorOption}
          loading={isSearching}
          noOptionsText="No vendor found"
          clearOnBlur={false}
          sx={{ flex: 1, minWidth: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter Vendor ID (e.g. V2913-3974-JOPL-5017)"
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {isSearching && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <ButtonV1
          title="Search"
          onClick={handleSearch}
          loader={isSearching}
          disabled={isSearching}
          size="small"
        />
      </Box>
    </Box>
  );
};

VendorSearch.propTypes = {
  vendor: PropTypes.object,
  onVendorChange: PropTypes.func.isRequired,
};

export default VendorSearch;
